import React, { useCallback, useEffect, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaCheckCircle, FaExclamationTriangle, FaPlay, FaRobot, FaSyncAlt,
  FaTrashAlt, FaRedo, FaUserPlus, FaExternalLinkAlt,
} from 'react-icons/fa';
import { TechButton, TechButtonGhost, TechPageHeader, TechShell, StatusPill } from '../../styles/techComponents';
import {
  createAutomationJob, getAutomationJobStatus, listAutomationJobs,
  deleteAutomationJob, retryAutomationJob,
} from '../../services/automationService';

const Grid = styled.div`
  display: grid;
  gap: 1.25rem;
`;

const Card = styled.section`
  padding: 1.15rem;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.012)),
    rgba(8, 15, 28, 0.72);
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  color: var(--text-secondary);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  input, select, textarea {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    background: rgba(255,255,255,0.035);
    color: var(--text-primary);
    padding: 0.75rem 0.85rem;
    outline: none;
  }
  textarea {
    min-height: 110px;
    resize: vertical;
    text-transform: none;
    letter-spacing: normal;
  }
`;

const Full = styled.div`
  grid-column: 1 / -1;
`;

const Alert = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(245, 158, 11, 0.32);
  background: rgba(245, 158, 11, 0.08);
  color: var(--text-secondary);
  line-height: 1.5;
  svg { color: #f59e0b; margin-top: 0.2rem; flex-shrink: 0; }
`;

const Timeline = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const LogLine = styled.div`
  padding: 0.6rem 0.85rem;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  background: rgba(0,0,0,0.18);
  color: var(--text-secondary);
  font-family: 'JetBrains Mono', 'Consolas', monospace;
  font-size: 0.76rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.65rem;
`;

const ProfileCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(255,255,255,0.08);
  border-left: 3px solid var(--accent-cyan, #00b4d8);
  border-radius: 8px;
  background: rgba(0,0,0,0.18);
  text-decoration: none;
  transition: border-color 0.15s, transform 0.15s;
  &:hover { border-color: var(--accent-cyan, #00b4d8); transform: translateY(-1px); }
  .u { color: var(--text-primary); font-weight: 600; font-size: 0.84rem; display: flex; align-items: center; gap: 5px; }
  .u svg { font-size: 0.62rem; color: var(--text-disabled); }
  .meta { font-family: 'JetBrains Mono', monospace; font-size: 0.64rem; color: var(--text-disabled); text-transform: uppercase; letter-spacing: 0.08em; }
`;

const JobRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(255,255,255,0.08);
  border-left: 3px solid ${(p) => p.$color || 'rgba(255,255,255,0.2)'};
  border-radius: 8px;
  background: rgba(0,0,0,0.18);
  cursor: pointer;
  &:hover { border-color: rgba(255,255,255,0.2); }
  .info { min-width: 0; }
  .info .top { color: var(--text-primary); font-size: 0.84rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .info .sub { font-family: 'JetBrains Mono', monospace; font-size: 0.66rem; color: var(--text-disabled); margin-top: 2px; }
  .row-actions { display: flex; gap: 0.4rem; }
`;

const Badge = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 2px 7px;
  border-radius: 5px;
  border: 1px solid ${(p) => p.$color || 'rgba(255,255,255,0.2)'};
  color: ${(p) => p.$color || 'var(--text-secondary)'};
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px; height: 30px;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.04);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { color: ${(p) => p.$danger ? '#ff5470' : 'var(--accent-cyan, #00b4d8)'}; border-color: ${(p) => p.$danger ? '#ff5470' : 'var(--accent-cyan, #00b4d8)'}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const STATUS_META = {
  idle: { color: 'var(--text-disabled)', label: 'idle' },
  queued: { color: '#f59e0b', label: 'en cola' },
  running: { color: '#00b4d8', label: 'procesando' },
  completed: { color: '#38d39f', label: 'completado' },
  failed: { color: '#ff5470', label: 'fallido' },
  blocked: { color: '#ff5470', label: 'bloqueado' },
};

const statusColor = (s) => (STATUS_META[s] || STATUS_META.idle).color;
const statusLabel = (s) => (STATUS_META[s] || STATUS_META.idle).label;

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const AutomationDashboard = () => {
  const [platform, setPlatform] = useState('instagram');
  const [quantity, setQuantity] = useState(3);
  const [persona, setPersona] = useState('');
  const [mode, setMode] = useState('sandbox');
  const [accepted, setAccepted] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const pollingRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const data = await listAutomationJobs(30);
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  const pollJob = (id) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const data = await getAutomationJobStatus(id);
        setJob(data.job);
        if (['completed', 'failed', 'blocked'].includes(data.job?.status)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          loadJobs();
        }
      } catch (err) {
        setFeedback({ type: 'error', message: friendlyError(err) });
      }
    }, 1800);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    setLoading(true);
    try {
      const data = await createAutomationJob({
        platform,
        config: { quantity: Number(quantity), mode, persona, complianceAccepted: accepted },
      });
      setJob(data.job);
      pollJob(data.job.id);
      loadJobs();
      setFeedback({ type: 'success', message: 'Trabajo creado y enviado a la cola de perfiles IA.' });
    } catch (err) {
      setFeedback({ type: 'error', message: friendlyError(err, 'No se pudo crear el trabajo.') });
    } finally {
      setLoading(false);
    }
  };

  const openJob = async (id) => {
    try {
      const data = await getAutomationJobStatus(id);
      setJob(data.job);
      if (['queued', 'running'].includes(data.job?.status)) pollJob(id);
    } catch { /* noop */ }
  };

  const handleRetry = async (id, ev) => {
    ev?.stopPropagation();
    try {
      const data = await retryAutomationJob(id);
      setJob(data.job);
      pollJob(id);
      loadJobs();
    } catch (err) {
      setFeedback({ type: 'error', message: friendlyError(err) });
    }
  };

  const handleDelete = async (id, ev) => {
    ev?.stopPropagation();
    try {
      await deleteAutomationJob(id);
      if (job?.id === id) setJob(null);
      loadJobs();
    } catch (err) {
      setFeedback({ type: 'error', message: friendlyError(err) });
    }
  };

  const logs = Array.isArray(job?.result?.logs) ? job.result.logs : [];
  const created = Array.isArray(job?.result?.created) ? job.result.created : [];
  const personas = Array.isArray(job?.result?.personas) ? job.result.personas : [];
  const summary = job?.result?.summary || null;

  return (
    <TechShell>
      <TechPageHeader
        title="Automatización de Perfiles IA"
        subtitle="Generá lotes de personalidades con IA y creá perfiles digitales en borrador, listos para cargar credenciales."
        dossier="DIGI://AI-PROFILE-JOBS"
      >
        <StatusPill><FaRobot /> Queue <strong>{statusLabel(job?.status || 'idle')}</strong></StatusPill>
      </TechPageHeader>

      <Grid>
        <Card>
          <Alert>
            <FaExclamationTriangle />
            <span>
              Esta sección genera <strong>personalidades IA</strong> y deja perfiles en <strong>borrador</strong> dentro
              de Perfiles Digitales — no registra cuentas ni evade verificaciones de terceros. Cargá credenciales o
              cookies manualmente, solo sobre cuentas propias, APIs oficiales o entornos sandbox.
            </span>
          </Alert>
        </Card>

        <Card>
          <Form onSubmit={handleSubmit}>
            <Field>
              Plataforma
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                <option value="instagram">Instagram</option>
                <option value="x">X / Twitter</option>
                <option value="facebook">Facebook</option>
                <option value="linkedin">LinkedIn</option>
                <option value="sandbox">Sandbox interno</option>
              </select>
            </Field>
            <Field>
              Cantidad
              <input type="number" min="1" max="10" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </Field>
            <Field>
              Modo
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="sandbox">Sandbox</option>
                <option value="official_api">API oficial autorizada</option>
              </select>
            </Field>
            <Full>
              <Field>
                Personalidad / briefing IA
                <textarea value={persona} onChange={(e) => setPersona(e.target.value)} placeholder="Ej: vecinos de San Nicolás interesados en política local, tono cercano, edades 25-45..." />
              </Field>
            </Full>
            <Full>
              <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} style={{ marginTop: 4 }} />
                Confirmo que el trabajo se ejecutará únicamente sobre cuentas propias, APIs oficiales o entornos autorizados, sin bypass de CAPTCHA ni violación de términos de servicio.
              </label>
            </Full>
            <Full>
              <Actions>
                <TechButton type="submit" disabled={loading || !accepted}>
                  {loading ? <><FaSyncAlt /> Creando...</> : <><FaPlay /> Generar lote</>}
                </TechButton>
                {feedback && (
                  <span style={{ color: feedback.type === 'success' ? 'var(--alert-success, #38d39f)' : 'var(--alert-error, #ff5470)' }}>
                    {feedback.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />} {feedback.message}
                  </span>
                )}
              </Actions>
            </Full>
          </Form>
        </Card>

        {job && (
          <Card>
            <SectionTitle>
              <FaRobot /> Trabajo #{String(job.id).slice(0, 8)}
              <Badge $color={statusColor(job.status)}>{statusLabel(job.status)}</Badge>
              <Badge>{job.platform}</Badge>
              {summary?.generation && <Badge $color="#00b4d8">{summary.generation === 'ia' ? 'IA' : summary.generation === 'mixed' ? 'IA+local' : 'local'}</Badge>}
            </SectionTitle>

            {created.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: '0.55rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaUserPlus /> {created.length} perfil(es) creado(s) en borrador
                </div>
                <ProfileGrid>
                  {created.map((p) => (
                    <ProfileCard key={p.id} to={`/perfiles-digitales/${p.id}`}>
                      <span className="u">{p.display_name || p.username} <FaExternalLinkAlt /></span>
                      <span className="meta">@{p.username} · {p.platform}</span>
                    </ProfileCard>
                  ))}
                </ProfileGrid>
              </div>
            )}

            {created.length === 0 && personas.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: '0.55rem' }}>
                  {personas.length} personalidad(es) generada(s) (sandbox)
                </div>
                <ProfileGrid>
                  {personas.map((p, i) => (
                    <div key={i} style={{ padding: '0.7rem 0.85rem', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, background: 'rgba(0,0,0,0.18)' }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.84rem' }}>{p.display_name || p.username}</div>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.64rem', color: 'var(--text-disabled)' }}>@{p.username}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>{(p.intereses || []).slice(0, 3).join(' · ')}</div>
                    </div>
                  ))}
                </ProfileGrid>
              </div>
            )}

            <Timeline>
              {logs.length === 0 && <LogLine>Esperando logs del worker...</LogLine>}
              {logs.map((line, idx) => <LogLine key={`${line}-${idx}`}>{line}</LogLine>)}
              {job.error && <LogLine style={{ color: 'var(--alert-error, #ff5470)' }}>{job.error}</LogLine>}
            </Timeline>
          </Card>
        )}

        <Card>
          <SectionTitle style={{ justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaRobot /> Historial de trabajos</span>
            <TechButtonGhost type="button" onClick={loadJobs}><FaSyncAlt /> Refrescar</TechButtonGhost>
          </SectionTitle>
          {jobs.length === 0 && <LogLine>Todavía no creaste ningún trabajo.</LogLine>}
          <Timeline>
            {jobs.map((j) => {
              const n = Array.isArray(j.result?.created) ? j.result.created.length : 0;
              const retryable = ['completed', 'failed', 'blocked'].includes(j.status);
              return (
                <JobRow key={j.id} $color={statusColor(j.status)} onClick={() => openJob(j.id)}>
                  <div className="info">
                    <div className="top">
                      <Badge $color={statusColor(j.status)}>{statusLabel(j.status)}</Badge>
                      <span>{j.platform}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                        {n > 0 ? `${n} perfil(es)` : (j.config?.quantity ? `${j.config.quantity} solicitado(s)` : '')}
                      </span>
                    </div>
                    <div className="sub">#{String(j.id).slice(0, 8)} · {fmtDate(j.created_at)}{j.config?.persona ? ` · "${String(j.config.persona).slice(0, 40)}"` : ''}</div>
                  </div>
                  <div className="row-actions">
                    <IconBtn type="button" title="Reintentar" disabled={!retryable} onClick={(e) => handleRetry(j.id, e)}><FaRedo /></IconBtn>
                    <IconBtn type="button" title="Eliminar" $danger onClick={(e) => handleDelete(j.id, e)}><FaTrashAlt /></IconBtn>
                  </div>
                </JobRow>
              );
            })}
          </Timeline>
        </Card>
      </Grid>
    </TechShell>
  );
};

export default AutomationDashboard;
